import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { fuseAnimations } from '@fuse/animations';
import { BaseComponent } from '../../base/base.component';
import { AuthService } from 'app/shared/services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { Subject } from 'app/shared/models/subject';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { enumReviewStatus } from 'app/shared/models/review';
import { take } from 'rxjs/operators';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { messageService } from 'app/shared/services/messages/message.module';
import { map } from 'rxjs/operators';
import { OrganisationService } from 'app/shared/services/organisation.service';
import { SubjectService } from 'app/shared/services/subject.service';
import { DateFormatPipe } from 'app/pipes/date-format.pipe';
import { HyperLawService } from 'app/shared/services/hyper-law.service';
import { MapComponent } from 'app/components/map/map.component';

@Component({
  selector: 'referrals',
  template: `<app-datagrid
    #appList
    [title]="title"
    [rows]="data$|async"
    [columns]="columns"
    [allowAdd]="allowAdd && !userReadOnly"
    [allowDelete]="admin"
    [selectionMode]="'multiple'"
    [type]="'Referral'"
    (onAdd)="add()"
    (onDelete)="delete($event)"
    (onEdit)="edit($event)"
    (onArchiveSelectedRows)="archiveSelectedRows($event)"
    (onDeleteSelectedRows)="deleteSelectedRows($event)"
    (onShowOnMap)="onShowOnMap($event)"
    fxFill
  >
  </app-datagrid>`,
  animations: fuseAnimations,
})
export class ReferralsComponent
  extends BaseComponent
  implements OnInit, OnDestroy {
  data: Subject[];
  data$: Observable<Subject[]>;
  columns = [];
  title = 'Referrals';
  allowAdd = false;
  userReadOnly = false;
  admin = false;
  onHyperLawConfigValidChanged: Subscription;

  constructor(
    private subjectService: SubjectService,
    private defaultDataService: DefaultDataService,
    private organisationService: OrganisationService,
    private authService: AuthService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private hyperLawService: HyperLawService,
    public dialog: MatDialog
  ) {
    super();

    this.userReadOnly = this.authService.isUserReadOnly();
    this.admin = this.authService.hasPriv('admin');
    this.allowAdd = this.defaultDataService.enableReferrals;

    this.data$ = this.subjectService.onChanged.pipe(map((data: Subject[]) => {
      data?.forEach((item: any) => {
        item.canDelete = this.admin;
        item.reviewStatusName = item.reviewStatus
          ? enumReviewStatus[item.reviewStatus]
          : enumReviewStatus.Pending;
        item.primaryLink = undefined;
        if (
          item.category &&
          item.category.primaryRelationshipId &&
          item.subjectLinks.length > 0
        ) {
          // MRD - #8415 - include more than one summary in primary link
          if (item.category.primaryRelationship) {
            const relationships =
              item.category.primaryRelationshipId.split(',');
            const links = [];
            relationships.forEach((relationshipId) => {
              const link = item.subjectLinks.find(
                (i) => i.relationship.id === Number(relationshipId)
              );
              if (link && link.linkedSubject) {
                links.push(link.linkedSubject.summary);
              }
            });
            item.primaryLink = links.length > 0 ? links.join(',') : undefined;
          }
        }
      });

      const filtered = data?.filter((i) => i.isReferral);
      this.data = [...filtered];
      return [...filtered];
    }));

    this.onHyperLawConfigValidChanged = this.hyperLawService.configValid.subscribe((configValid: boolean) => {
      if (configValid) {
        this.columns.push({
          name: 'Case Preparation Added',
          prop: 'hasCasePreparationFile',
          dataType: 'boolean',
        });
        this.columns = [...this.columns];
      }
    });
  }

  ngOnInit() {
    this.columns = [
      { name: 'Status', prop: 'reviewStatusName', dataType: 'reviewStatus' },
      {
        name: 'Referral Date',
        prop: 'referral.referralDate',
        dataType: 'date',
      },
      { name: 'Reference', prop: 'reference' },
      { name: 'Summary', prop: 'summary' },
      { name: 'Source', prop: 'referral.referralSource.name' },
      { name: 'Type', prop: 'subjectType.name' },
      { name: 'Category', prop: 'category.description' },
      { name: 'Assigned To', prop: 'subjectUsers[0].user.fullName' },
      { name: 'Referrer', prop: 'referral.referrerName' },
      { name: 'Manager', prop: 'referral.managerName' },
      { name: 'Review Date', prop: 'reviewDate', dataType: 'date' },
      {
        name: 'Assessment',
        prop: 'assessmentScore',
        dataType: 'assessmentScore',
      },
      { name: 'Primary Link', prop: 'primaryLink' },
    ];

    // at least one referral has an organisation specified, add organisation column if required...
    const colName = this.organisationService.organisationLabel();
    if (!this.columns.find((i) => i.name === colName)) {
      this.columns.push({
        name: this.organisationService.organisationLabel(),
        prop: 'subjectOrganisations[0].organisation.name',
      });
      //this.columns = [...this.columns];
      // setTimeout(() => {
      //   this.data = [...this.data];
      // }, 100);
    }

    // set title
    const s = this.activeRoute.snapshot.url.toString() + ' ';
    const archived =
      s.indexOf(',archived ') > -1 || s.indexOf(',archived,') > -1
        ? 'Archived '
        : '';
    const dueForDeletion =
      s.indexOf(',duefordeletion ') > -1 || s.indexOf(',duefordeletion,') > -1
        ? ' Due For Deletion'
        : '';
    this.title = archived + 'Referrals' + dueForDeletion;
    const my = s.indexOf(',my ') > -1 || s.indexOf(',my,') > -1 ? 'My ' : '';
    const all =
      s.indexOf(',all ') > -1 || s.indexOf(',all,') > -1 ? 'All ' : '';
    this.title = archived + my + all + 'Referrals' + dueForDeletion;

    // hide the add button is archived or due for deletion list
    this.allowAdd = this.allowAdd && (archived + dueForDeletion).length === 0;

    if ((archived + dueForDeletion).length > 0) {
      this.columns.push({
        name: 'Archived Date',
        prop: 'dateArchived',
        dataType: 'date',
      });
    }

    if (this.hyperLawService.isConfigValid()) {
      this.columns.push({
        name: 'Case Preparation Added',
        prop: 'hasCasePreparationFile',
        dataType: 'boolean',
      });
    }

    this.columns = [...this.columns];
  }

  ngOnDestroy(): void {
    this.onHyperLawConfigValidChanged?.unsubscribe();
  }

  add() {
    this.router.navigateByUrl(`/referrals/referrals/subject/new`);
  }

  edit(row) {
    const subject = this.data.find((x) => x.id === row.id);
    if (subject) {
      // navigate to referral subject dashboard for primary subject
      this.router.navigateByUrl(`/referrals/referrals/subject/${subject.uid}`);
    }
  }

  delete(row) {
    // by this stage user has already confirmed the delete, so remove item from data source
    const info = new MessageBoxParameters(
      'The Referral and related data will be permanently deleted.  Are you sure you want to continue?',
      'Warning',
      MessageBoxType.YesNo,
      true,
      true
    );
    this.dialog
      .open(MessageBoxComponent, {
        data: info,
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'yes') {
          // Get UID for selected row being deleted and delete
          const editrow = this.data.find((x) => x.id === row.id);
          this.subjectService.delete(editrow).subscribe((_) => {
            // if the service returns null then the delete failed
            if (_ !== null) {
              // remove the deleted item from the data
              this.data = this.data.filter((item) => item !== row);

              if (row?.hasCasePreparationFile) {                
                this.hyperLawService.deleteCase(row as Subject);
              }
            }
          });
        }
      });
  }

  archiveSelectedRows(keys) {
    const selectedRows = this.data.filter((i) => keys.indexOf(i.id) > -1);
    if (selectedRows.length > 0) {
      const info = new MessageBoxParameters(
        `Are you sure you want to archive the ${selectedRows.length} selected referrals?`,
        'Confirm',
        MessageBoxType.Confirm,
        true
      );
      this.dialog
        .open(MessageBoxComponent, {
          data: info,
          disableClose: false,
          autoFocus: false,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result === 'confirm') {
            const save$ = [];
            const success = [];
            selectedRows.forEach((i) => {
              i.archived = true;
              i.dateArchived = new DateFormatPipe('en-GB').transform(
                new Date(Date.now())
              );
              
              // Do not pass this data back as it can stop the save taking place with SQL Delete errors in some cases. 
              // This is also more performance as it transfers less data as only the Archive flag and ArchiveDate have changed and need saving.
              var s = new Subject(i);
              s.subjectReviews = undefined;
              s.subjectAssessments = undefined;
              s.linkedSubjectMarkers = undefined;
              s.subjectLinks = undefined;
              s.subjectReferrals = undefined;
              s.subjectMarkers = undefined;
              s.subjectOrganisations = undefined;
              s.subjectReviews = undefined;
              s.subjectTemplates = undefined;
              s.subjectUsers = undefined;

              save$.push(
                this.subjectService.saveAsObservable(s, '', false).pipe(
                  map((subject: Subject) => {
                    if (subject !== null) {
                      success.push(subject.id);
                    }
                  })
                )
              );
            });
            forkJoin(save$)
              .pipe(take(1))
              .subscribe((_) => {
                if (success.length === selectedRows.length) {
                  messageService.success(
                    `The selected Referrals have been archived`
                  );
                } else {
                  const failed = selectedRows.length - success.length;
                  messageService.error(
                    `${failed} Referrals were not archived.  Please review any error messages and try again.`
                  );
                }

                // remove archived records from the current list
                const currentData = this.subjectService.onChanged.getValue();
                this.subjectService.onChanged.next([...currentData.filter((i) => success.indexOf(i.id) === -1)]);
              });
          }
        });
    } else {
      console.log('Archive selected referrals found no matches:', keys);
    }
  }

  deleteSelectedRows(keys) {
    const selectedRows = this.data.filter((i) => keys.indexOf(i.id) > -1);
    if (selectedRows.length > 0) {
      const info = new MessageBoxParameters(
        `The ${selectedRows.length} selected Referrals and their related data will be permanently deleted.  Are you sure you want to continue?`,
        'Warning',
        MessageBoxType.YesNo,
        true,
        true
      );
      this.dialog
        .open(MessageBoxComponent, {
          data: info,
          disableClose: false,
          autoFocus: false,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result === 'yes') {
            const delete$ = [];
            const success = [];
            selectedRows.forEach((i) => {
              delete$.push(
                this.subjectService.delete(i).pipe(
                  map((entity: Subject) => {
                    // if the service returns null then the delete failed
                    if (entity !== null) {
                      success.push(entity.id);
                    }
                  })
                )
              );
            });
            forkJoin(delete$)
              .pipe(take(1))
              .subscribe((_) => {
                // notify outcome to user
                if (success.length === selectedRows.length) {
                  messageService.success(
                    `The selected Referrals have been deleted`
                  );
                } else {
                  const failed = selectedRows.length - success.length;
                  messageService.error(
                    `${failed} Referrals were not deleted.  Please review the error messages and try again.`
                  );
                }
                // remove successfully deleted records from the current list
                this.data = [
                  ...this.data.filter((i) => success.indexOf(i.id) === -1),
                ];
              });
          }
        });
    } else {
      console.log('Delete selected rows found no matches:', keys);
    }
  }

  onShowOnMap(e)
  {
    this.dialog.open(MapComponent, {
      data: {
        subjectList: e
      }
    });
  }
}
